
import { KolCategory, OrderStatus, SocialPlatform, Language } from './types';

// Map of Language Codes to Native Names
export const LANGUAGE_NAMES: Record<Language, string> = {
  en: 'English',
  zh: '简体中文',
  'zh-TW': '繁體中文',
  ko: '한국어',
  ja: '日本語',
  vi: 'Tiếng Việt',
  ru: 'Русский',
  tr: 'Türkçe',
  fr: 'Français',
  es: 'Español',
  ar: 'العربية',
  de: 'Deutsch',
  id: 'Bahasa Indonesia',
  ms: 'Bahasa Melayu',
  th: 'ไทย',
  pt: 'Português',
  it: 'Italiano',
  hi: 'हिन्दी'
};

export const getStatusLabel = (status: OrderStatus, lang: Language): string => {
  // Simple mapping for demo, ideal would be inside the dictionary
  if (lang === 'zh') {
    switch (status) {
      case OrderStatus.PENDING_ACCEPTANCE: return '等待接受';
      case OrderStatus.NEGOTIATING: return '谈判中';
      case OrderStatus.ESCROW_PENDING: return '待托管';
      case OrderStatus.IN_PROGRESS: return '进行中';
      case OrderStatus.SUBMITTED: return '已提交';
      case OrderStatus.REVIEW_DATA: return '审核数据';
      case OrderStatus.COMPLETED: return '已完成';
      case OrderStatus.DISPUTED: return '争议中';
      default: return status;
    }
  }
  return status;
};

export const getPlatformLabel = (platform: SocialPlatform, lang: Language): string => {
    if (lang === 'zh' && platform === SocialPlatform.RED_BOOK) return '小红书';
    return platform;
};

export const getCategoryLabel = (cat: KolCategory, lang: Language): string => {
    if (lang === 'zh') {
        const map: Record<string, string> = {
            [KolCategory.ALL]: '全部',
            [KolCategory.DEFI]: 'DeFi',
            [KolCategory.NFT]: 'NFT',
            [KolCategory.GAMEFI]: '链游',
            [KolCategory.L1_L2]: 'L1/L2',
            [KolCategory.MEME]: 'Meme/土狗',
            [KolCategory.TRADING]: '交易分析',
            [KolCategory.LIFESTYLE]: '生活方式',
            [KolCategory.TECH]: '硬核技术'
        };
        return map[cat] || cat;
    }
    return cat;
};

const en = {
    // Landing Page
    landingTitle: "Marketing Logic for Web3",
    landingSubtitle: "Stop burning budget on bots. LinkKol connects you with verified crypto influencers via smart contracts for guaranteed results.",
    landingTicker1: "⚡ @CryptoWiz just earned 800 USDT",
    landingTicker2: "🚀 New Campaign: DeFi Protocol Launch ($15k Budget)",
    landingTicker3: "💎 @NFT_Hunter completed a 5.2x ROI campaign",
    landingTicker4: "🔥 125 New KOLs joined today",
    
    iAmProject: "I'm a Project",
    projectTagline: "I want verified traffic & ROI.",
    projectStat: "Avg. ROI: 3.5x",
    projectBenefit: "• AI-filtered authentic KOLs\n• Smart Contract Escrow\n• On-chain conversion tracking",
    
    iAmKol: "I'm a Creator",
    kolTagline: "I want secure payments & gigs.",
    kolStat: "Payout: Instant",
    kolBenefit: "• No chasing payments\n• Build on-chain reputation\n• Earn Tokens + USDT",

    enterApp: "Launch App",
    trustedBy: "Supported Chains & Ecosystems",
    quickStart: "Quick Start with Socials",
    loginWith: "Login with",
    
    feature1Title: "Data, Not Hype",
    feature1Desc: "Our AI scans blockchain data to verify if a KOL's followers actually buy tokens, not just 'like' tweets.",
    feature2Title: "Trustless Escrow",
    feature2Desc: "Funds are locked on-chain. KOLs get paid automatically when milestones are met. No rug pulls.",
    feature3Title: "Hybrid Payments",
    feature3Desc: "Pay with USDT or your native project tokens. Vesting schedules enforced by smart contracts.",
    
    howItWorks: "How it Works",
    step1Title: "Connect & Verify",
    step1Desc: "Link your social accounts and wallet. Our AI verifies your ownership and audience quality instantly.",
    step2Title: "Match & Contract",
    step2Desc: "Find the perfect partner. Agree on deliverables and lock funds in a smart contract escrow.",
    step3Title: "Execute & Earn",
    step3Desc: "Deliver the work. Once verified on-chain, funds are released automatically. No invoicing needed.",

    statsUsers: "Active Users",
    statsVolume: "Total Volume",
    
    // Roles
    projectRole: "Project View",
    kolRole: "KOL View",
    adminRole: "Admin View",
    switchToKol: "Switch to KOL View",
    switchToProject: "Switch to Project View",
    switchToAdmin: "Switch to Admin View",

    // Sidebar
    dashboard: "Dashboard",
    marketplace: "Marketplace",
    squads: "KOL Squads", 
    disputeCenter: "Dispute Center", 
    findProjects: "Find Projects",
    postCampaign: "Post Campaign", 
    myCampaigns: "My Campaigns", 
    contracts: "Contracts",
    analytics: "Analytics",
    settings: "Settings",
    yourBalance: "Your Balance",
    myProfile: "My Profile",
    messages: "Messages", 
    adminDashboard: "Admin Dashboard",
    userManagement: "Users",
    financials: "Financials",
    referrals: "Refer & Earn",

    // Header
    network: "Network",
    connectWallet: "Connect Wallet",
    walletConnected: "0x12...8f4A",

    // Dashboard (Project)
    overview: "Overview",
    welcomeBack: "Welcome back, SuperNova Protocol",
    downloadReport: "Download Report",
    totalSpent: "Total Spent",
    activeContracts: "Active Contracts",
    totalImpressions: "Total Impressions",
    avgRoi: "Avg ROI",
    vsLastMonth: "vs last month",
    campaignPerformance: "Campaign Performance",
    recentEscrow: "Recent Escrow Activity",
    viewAllContracts: "View All Contracts",
    week: "Week",
    campaignCalendar: "Content Calendar",
    addEvent: "Add Event", 

    // Dashboard (KOL)
    kolOverview: "Creator Dashboard",
    welcomeKol: "Welcome back, Alpha Creator",
    totalEarnings: "Total Earnings",
    pendingOrders: "Pending Orders",
    avgEngagement: "Avg Engagement",
    profileVisits: "Profile Visits",
    earningsOverTime: "Earnings Over Time",
    platformDistribution: "Audience Distribution",
    recentRequests: "Recent Collaboration Requests",
    accept: "Accept",
    decline: "Decline",

    // KOL Levels (Gamification)
    currentLevel: "Current Level",
    nextLevel: "Next Level",
    pointsToGo: "Points to Go",
    levelBenefits: "Level Benefits",
    lvl_1: "Novice",
    lvl_2: "Explorer",
    lvl_3: "Rising Star",
    lvl_4: "Influencer",
    lvl_5: "KOL",
    lvl_6: "Trendsetter",
    lvl_7: "Crypto Native",
    lvl_8: "Whale Whisperer",
    lvl_9: "Market Mover",
    lvl_10: "Legend",

    // LinkKol Token Sale
    kolRoundTitle: "LinkKol Community Round",
    exclusiveAccess: "KOL 专属通道",
    tokenPrice: "Token Price",
    valuation: "FDV",
    vestingSchedule: "Vesting Schedule",
    vestingDetails: "50% TGE, 50% linear over 12 months",
    buybackLogic: "Deflationary Mechanism",
    buybackDesc: "50% of platform revenue is used to buy back & burn LinkKol tokens monthly.",
    investNow: "Invest Now",
    allocation: "Allocation",
    maxAllocation: "Max Allocation",
    raised: "Raised",
    investSuccess: "Investment Successful!",
    investDesc: "You have secured your allocation. Tokens will be visible in your wallet at TGE.",
    round: "Round",
    tokensSold: "Tokens Sold",
    limitPerKol: "Limit / KOL",
    tokens: "Tokens",
    selectRound: "Select Round",
    usdtInvested: "USDT Invested",
    tokensSecured: "Tokens Secured",

    // Dashboard (Admin)
    adminOverview: "Platform Admin",
    welcomeAdmin: "Welcome, Administrator",
    totalRevenue: "Total Revenue (20%)",
    totalGmv: "Total GMV",
    totalUsers: "Total Users",
    activeDisputes: "Active Disputes",
    recentTransactions: "Recent Transactions",
    commissionRate: "Commission Rate",
    netToKol: "Net to You", 
    platformFee: "Platform Fee",
    transactionId: "Transaction ID",
    tokenSalesAnalytics: "Token Sales Analytics",
    kolIntelligence: "KOL Intelligence",
    roundProgress: "Round Progress",
    totalRaisedUsdt: "Total USDT Raised",
    totalTokensSold: "Total Tokens Sold",
    inviter: "Invited By",
    inviteCount: "Invites",
    tokensHeld: "Tokens Held",
    earnings: "Earnings",
    
    // Disputes
    disputeCenterTitle: "Decentralized Justice",
    disputeCenterSubtitle: "AI-assisted dispute resolution with Admin final oversight.",
    openDispute: "Open Dispute",
    evidence: "Evidence",
    voteRefund: "Vote Refund (Project)",
    voteRelease: "Vote Release (KOL)",
    amountLocked: "Amount Locked",
    initiator: "Initiator",
    respondent: "Respondent",
    reason: "Reason",
    votes: "Votes",
    castVote: "Cast Your Vote",
    selectDispute: "Select a dispute to view details",
    aiJudge: "Preliminary AI Verdict",
    aiConfidence: "Confidence Score",
    aiRecommendation: "Recommended Verdict",
    aiReasoning: "AI Reasoning",
    chatHistory: "Chat History",
    adminDecision: "Admin Final Verdict",
    finalizeRefund: "Finalize: Refund Project",
    finalizeRelease: "Finalize: Release to KOL",
    adminReasonLabel: "Admin Judgment Reason",
    enterReasonPlaceholder: "Please explain your verdict (visible to both parties)...",
    confirmVerdict: "Confirm Verdict",
    verdictReason: "Verdict Reason",
    resolvedOn: "Resolved on",
    
    // Squads
    squadMarketplace: "KOL Squads",
    squadSubtitle: "Book pre-vetted teams for massive impact.",
    combinedReach: "Combined Reach",
    bundlePrice: "Bundle Price",
    saveAmount: "You Save",
    bookSquad: "Book Squad",
    squadMembers: "Squad Members",
    createSquad: "Create Squad",
    squadName: "Squad Name",
    squadDescription: "Description",
    discount: "Discount (%)",
    addMembers: "Add Members",
    squadCreated: "Squad Created Successfully!",
    leaderVerified: "Leader Verified",
    trustScore: "Trust Score",
    leaderExplanation: "You will be the Squad Leader. You earn a 5% management fee on top of your rate.",
    squadRequirements: "Squad Leader Requirements",
    increaseTrustDesc: "Complete more orders successfully to increase your Trust Score.",
    squadDetails: "Squad Details",
    meetTheSquad: "Meet the Squad",
    requestToJoin: "Request to Join",
    applicationSent: "Application Sent!",
    alreadyMember: "Already a Member",
    manageApplications: "Manage Applications",
    pendingApplications: "Pending Applications",
    noApplications: "No pending applications.",
    applicationReason: "Application Reason",
    whyJoinSquad: "Why do you want to join this squad?",
    approve: "Approve",
    reject: "Reject",
    joinRequestSent: "Join Request Sent",
    joinRequestDesc: "Your application has been sent to the Squad Leader for review.",
    revenueSplit: "Revenue Distribution",
    myRate: "My Squad Rate",
    myRateDesc: "Your share of the total value before discount.",
    estimatedPayout: "Est. Payout",
    proportionalShare: "Share of Net",
    leaderFee: "Leader Fee (5%)",
    platformFeeLabel: "Platform Fee (20%)",
    enterLeaderRate: "Your Rate (Leader)",
    enterProposedRate: "Proposed Rate (USDT)",
    totalSquadValue: "Total Squad Value",
    
    // Squad Booking
    squadBookingTitle: "Book Squad Campaign",
    squadSummary: "Squad Summary",
    totalPrice: "Total Price",
    campaignRequirements: "Campaign Requirements",
    campaignDeadline: "Deadline",
    confirmBooking: "Confirm Booking",
    bookingSuccess: "Squad Booked Successfully!",

    // Referral
    inviteEarn: "Invite & Earn",
    referralSubtitle: "Share your unique link. Earn 5% commission on platform fees from anyone you invite, forever.",
    referralSubtitleProject: "Invite high-quality KOLs to the platform. Earn 5% commission on their earnings from any project.",
    yourLink: "Your Referral Link",
    totalInvites: "Total Invites",
    commissionEarned: "Commission Earned",
    recentInvites: "Recent Invites",

    // Marketplace (Project finding KOL)
    findTalent: "Find Talent",
    findTalentSubtitle: "Data-driven discovery. No more guessing.",
    searchPlaceholder: "Search by name, tag...",
    trust: "Trust",
    followers: "Followers",
    engScore: "Eng. Score",
    estPrice: "Est. Price",
    viewProfile: "View Profile",
    backToMarketplace: "Back to Marketplace",
    sortBy: "Sort By",
    sortRecommended: "Recommended",
    sortTrustHigh: "Trust Score (High to Low)",
    sortFollowersHigh: "Followers (High to Low)",
    sortPriceLow: "Price (Low to High)",

    // Project Marketplace (KOL finding Project)
    findProjectsTitle: "Find Projects",
    findProjectsSubtitle: "Browse active campaigns and apply directly.",
    searchProjectsPlaceholder: "Search projects...",
    activeCampaigns: "Active Campaigns",
    grabMode: "Grab Mode",
    applyNow: "Apply Now",
    budgetRange: "Budget Range",
    
    // My Campaigns
    backToDashboard: "Back to Dashboard",
    campaignBudget: "Campaign Budget",
    escrowed: "Escrowed",
    notEscrowed: "Not Escrowed",
    applicants: "Applicants",
    proposedRate: "Proposed Rate",
    sendMessage: "Send Message",
    acceptOffer: "Accept Offer",
    noApplicants: "No applicants found.",
    viewApplicants: "View Applicants",

    // Orders
    contractManagement: "Contract Management",
    kolColumn: "KOL",
    projectColumn: "Project",
    campaignColumn: "Campaign",
    statusColumn: "Status",
    amountColumn: "Amount",
    dueDateColumn: "Due Date",
    actionsColumn: "Actions",
    details: "Details",
    leaveReview: "Leave Review",
    reviewSubmitted: "Review Submitted",

    // Contract Status & Steps
    stepPending: "Waiting for Acceptance",
    stepNegotiating: "Negotiating",
    stepEscrow: "Escrow",
    stepInProgress: "In Progress",
    stepSubmitted: "Submitted",
    stepCompleted: "Completed",
    contractProgress: "Contract Progress",
    autoReleaseTimer: "Auto-Release Timer",
    autoReleaseDesc: "Funds will be released automatically if no action is taken.",
    markAsDone: "Submit Work",
    confirmRelease: "Confirm & Release",
    enterArbitration: "Enter Arbitration",
    submissionReceived: "Submission Received",
    waitingForApproval: "Waiting for Project Approval",

    // Booking & Staking
    bookServiceTitle: "Book Service",
    stakingRequired: "Staking Required",
    stakingDesc: "To ensure trust, the full amount must be staked to the smart contract escrow. Funds are only released when work is approved.",
    serviceDetails: "Service Details",
    totalToStake: "Total to Stake",
    stakeAndBook: "Stake & Book",
    insufficientBalance: "Insufficient Balance",
    confirmStaking: "Confirm Staking",
    stakingProcessing: "Processing Staking...",
    bookingSent: "Booking Sent!",
    bookingSentDesc: "Funds have been staked to Escrow. Waiting for KOL acceptance.",

    // Monitoring
    monitoringDashboard: "Contract Details",
    aiSupervisor: "AI Supervisor",
    statusNormal: "Normal",
    statusRisk: "At Risk",
    statusCritical: "Critical",
    views: "Views",
    current: "Current",
    target: "Target",
    likes: "Likes",
    engagement: "Engagement",
    timeElapsed: "Time Elapsed",
    liveTracking: "Live Tracking",

    // Service Modal
    editService: "Edit Service",
    addService: "Add Service",
    serviceTitle: "Service Title",
    selectPlatform: "Select Platform",
    priceUsdt: "Price (USDT)",
    deliveryTime: "Delivery Time (Days)",
    description: "Description",
    featuresList: "Features (one per line)",
    cancel: "Cancel",
    saveService: "Save Service",
    delivery: "Delivery",
    bookNow: "Book Now",

    // Profile Edit
    editProfile: "Edit Profile",
    editProfileTitle: "Edit Profile",
    displayName: "Display Name",
    handle: "Handle",
    bio: "Bio",
    tags: "Tags",
    minPrice: "Min Price",
    maxPrice: "Max Price",
    saveChanges: "Save Changes",
    aiTags: "Auto-Tag with AI",
    aiTagsAnalyzing: "Analyzing...",
    tagsPlaceholder: "Press Enter to add tags",

    // Platform Connect
    connectPlatformTitle: "Connect Social Account",
    verifyAccount: "Verify your ownership of this",
    startVerification: "Start Verification",
    verifying: "Verifying...",
    fetchData: "Fetching data from",
    verifiedSuccess: "Verified Successfully!",
    connect: "Connect",

    // Review
    reviewTitle: "Leave a Review",
    reviewFor: "Review for",
    rating: "Rating",
    comment: "Comment",
    commentPlaceholder: "Share your experience working with this KOL...",
    submitReview: "Submit Review",

    // Apply
    applyTo: "Apply to",
    pitchMessage: "Pitch Message",
    pitchPlaceholder: "Why are you a good fit for this campaign?",
    sendApplication: "Send Application",

    // Settings
    generalSettings: "General",
    walletSettings: "Wallet",
    notificationSettings: "Notifications",
    securitySettings: "Security",
    accountInfo: "Account Information",
    email: "Email Address",
    language: "Language",
    currency: "Currency",
    connectedWallet: "Connected Wallet",
    disconnectWallet: "Disconnect",
    emailNotifications: "Email Notifications",
    pushNotifications: "Push Notifications",
    marketingEmails: "Marketing Emails",
    telegramSettings: "Telegram Integration",
    telegramHandle: "Telegram Handle",
    telegramConnected: "Connected",
    connectBot: "Connect Bot",
    twoFactorAuth: "Two-Factor Authentication",
    changePassword: "Change Password",
    savePreferences: "Save Preferences",
    preferencesSaved: "Saved",

    // Chat
    chats: "Chats",
    searchContacts: "Search contacts...",
    online: "Online",
    typeMessage: "Type a message...",
    noChatSelected: "Select a chat to start messaging",
    translatedMessage: "Translated",

    // Helpers
    unknown: "Unknown",
    
    // AI Assistant
    chatWithAi: "LinkKol AI Assistant",
    askAi: "Ask AI",
    askAiPlaceholder: "Ask about KOL performance...",
    aiSummary: "AI Summary",
    strengths: "Strengths",
    weaknesses: "Weaknesses",
    clientScore: "Client Score",
    aiScore: "AI Score",
    
    // AI Analysis Metrics (New)
    authenticity: "Authenticity",
    sentiment: "Sentiment",
    commercialValue: "Commercial",
    brandSafety: "Brand Safety",
    consistency: "Consistency",

    // LinkKol Points
    linkKolPoints: "LinkKol Points",
    pointsBalance: "Points Balance",
    viewRewards: "View Rewards",
    convertToToken: "Convert to LNK",
    comingSoon: "Coming Soon",
    pointsEarned: "Points Earned",
    
    // Other
    noReviews: "No reviews yet.",
    performanceHistory: "Performance History",
    clientReviews: "Client Reviews",
    services: "Services",
    servicesSubtitle: "Standard packages offered by this Creator",
    telegramAlertSent: "Telegram alert sent to"
};

const zh = {
    // Landing Page
    landingTitle: "Web3 营销的底层逻辑",
    landingSubtitle: "停止在假粉上浪费预算。LinkKol 通过智能合约将您与经过验证的加密影响者连接起来，保证结果。",
    landingTicker1: "⚡ @CryptoWiz 刚刚赚取了 800 USDT",
    landingTicker2: "🚀 新活动：DeFi 协议发布（$15k 预算）",
    landingTicker3: "💎 @NFT_Hunter 完成了 5.2 倍 ROI 的活动",
    landingTicker4: "🔥 今天有 125 位新 KOL 加入",
    
    iAmProject: "我是项目方",
    projectTagline: "我需要真实流量和回报率。",
    projectStat: "平均 ROI: 3.5x",
    projectBenefit: "• AI 过滤真实 KOL\n• 智能合约托管\n• 链上转化追踪",
    
    iAmKol: "我是创作者",
    kolTagline: "我需要安全的支付和订单。",
    kolStat: "支付：即时",
    kolBenefit: "• 拒绝拖欠款项\n• 建立链上声誉\n• 赚取代币 + USDT",

    enterApp: "启动应用",
    trustedBy: "支持的链与生态",
    quickStart: "社交账号快速登录",
    loginWith: "登录方式",

    feature1Title: "数据，而非炒作",
    feature1Desc: "我们的 AI 扫描链上数据，验证 KOL 的粉丝是否真的购买代币，而不仅仅是'点赞'推文。",
    feature2Title: "无信任托管",
    feature2Desc: "资金锁定在链上。当里程碑达成时，KOL 自动获得报酬。没有跑路风险。",
    feature3Title: "混合支付",
    feature3Desc: "使用 USDT 或您的项目原生代币支付。归属时间表由智能合约强制执行。",
    
    howItWorks: "工作原理",
    step1Title: "连接与验证",
    step1Desc: "连接您的社交账户和钱包。我们的 AI 即时验证您的所有权和受众质量。",
    step2Title: "匹配与签约",
    step2Desc: "寻找完美的合作伙伴。商定交付成果并将资金锁定在智能合约托管中。",
    step3Title: "执行与收益",
    step3Desc: "交付工作。一旦链上验证通过，资金将自动释放。无需发票。",

    statsUsers: "活跃用户",
    statsVolume: "总交易量",

    // Roles
    projectRole: "项目方视图",
    kolRole: "KOL 视图",
    adminRole: "管理员视图",
    switchToKol: "切换到 KOL 视图",
    switchToProject: "切换到项目方视图",
    switchToAdmin: "切换到管理员视图",

    // Sidebar
    dashboard: "仪表盘",
    marketplace: "市场",
    squads: "KOL 战队",
    disputeCenter: "争议中心",
    findProjects: "寻找项目",
    postCampaign: "发布活动",
    myCampaigns: "我的活动",
    contracts: "合约管理",
    analytics: "数据分析",
    settings: "设置",
    yourBalance: "您的余额",
    myProfile: "我的资料",
    messages: "消息",
    adminDashboard: "管理后台",
    userManagement: "用户管理",
    financials: "财务统计",
    referrals: "邀请赚钱",

    // Header
    network: "网络",
    connectWallet: "连接钱包",
    walletConnected: "已连接",

    // Dashboard (Project)
    overview: "概览",
    welcomeBack: "欢迎回来，SuperNova Protocol",
    downloadReport: "下载报告",
    totalSpent: "总支出",
    activeContracts: "进行中合约",
    totalImpressions: "总曝光",
    avgRoi: "平均 ROI",
    vsLastMonth: "对比上月",
    campaignPerformance: "活动表现",
    recentEscrow: "近期托管动态",
    viewAllContracts: "查看所有合约",
    week: "周",
    campaignCalendar: "内容日历",
    addEvent: "添加事件",

    // Dashboard (KOL)
    kolOverview: "创作者仪表盘",
    welcomeKol: "欢迎回来，Alpha Creator",
    totalEarnings: "总收入",
    pendingOrders: "待处理订单",
    avgEngagement: "平均互动",
    profileVisits: "主页访问",
    earningsOverTime: "收入趋势",
    platformDistribution: "受众分布",
    recentRequests: "近期合作请求",
    accept: "接受",
    decline: "拒绝",

    // KOL Levels
    currentLevel: "当前等级",
    nextLevel: "下一等级",
    pointsToGo: "升级所需",
    levelBenefits: "等级权益",
    lvl_1: "新手",
    lvl_2: "探索者",
    lvl_3: "新星",
    lvl_4: "影响者",
    lvl_5: "KOL",
    lvl_6: "潮流引领者",
    lvl_7: "加密原生",
    lvl_8: "巨鲸耳语者",
    lvl_9: "市场推动者",
    lvl_10: "传奇",

    // LinkKol Token Sale
    kolRoundTitle: "LinkKol 社区轮",
    exclusiveAccess: "KOL 专属通道",
    tokenPrice: "代币价格",
    valuation: "完全稀释估值 (FDV)",
    vestingSchedule: "归属时间表",
    vestingDetails: "50% TGE 解锁，50% 12个月线性解锁",
    buybackLogic: "通缩机制",
    buybackDesc: "平台收入的 50% 用于每月回购并销毁 LinkKol 代币。",
    investNow: "立即投资",
    allocation: "额度",
    maxAllocation: "最大额度",
    raised: "已筹集",
    investSuccess: "投资成功！",
    investDesc: "您已锁定额度。代币将在 TGE 时显示在您的钱包中。",
    round: "轮次",
    tokensSold: "已售代币",
    limitPerKol: "单人限额",
    tokens: "代币",
    selectRound: "选择轮次",
    usdtInvested: "投入 USDT",
    tokensSecured: "获得代币",

    // Dashboard (Admin)
    adminOverview: "平台管理",
    welcomeAdmin: "欢迎，管理员",
    totalRevenue: "总收入 (20%)",
    totalGmv: "总交易额 (GMV)",
    totalUsers: "总用户数",
    activeDisputes: "活跃争议",
    recentTransactions: "近期交易",
    commissionRate: "佣金率",
    netToKol: "KOL 实收",
    platformFee: "平台费",
    transactionId: "交易 ID",
    tokenSalesAnalytics: "代币销售分析",
    kolIntelligence: "KOL 情报",
    roundProgress: "轮次进度",
    totalRaisedUsdt: "总筹集 USDT",
    totalTokensSold: "总售出代币",
    inviter: "邀请人",
    inviteCount: "邀请数量",
    tokensHeld: "持有代币",
    earnings: "收益",

    // Disputes
    disputeCenterTitle: "去中心化法庭",
    disputeCenterSubtitle: "AI 辅助争议解决，管理员最终复核。",
    openDispute: "发起争议",
    evidence: "证据",
    voteRefund: "投票退款 (给项目方)",
    voteRelease: "投票放款 (给 KOL)",
    amountLocked: "锁定金额",
    initiator: "发起人",
    respondent: "被诉人",
    reason: "原因",
    votes: "票数",
    castVote: "投票",
    selectDispute: "选择争议查看详情",
    aiJudge: "AI 初审判决",
    aiConfidence: "置信度",
    aiRecommendation: "建议判决",
    aiReasoning: "AI 判决理由",
    chatHistory: "聊天记录",
    adminDecision: "管理员终审",
    finalizeRefund: "终审：退款给项目方",
    finalizeRelease: "终审：放款给 KOL",
    adminReasonLabel: "管理员判决理由",
    enterReasonPlaceholder: "请解释您的判决（双方可见）...",
    confirmVerdict: "确认判决",
    verdictReason: "判决理由",
    resolvedOn: "解决于",

    // Squads
    squadMarketplace: "KOL 战队",
    squadSubtitle: "预定经过验证的团队，获得巨大影响力。",
    combinedReach: "总覆盖人数",
    bundlePrice: "打包价",
    saveAmount: "节省",
    bookSquad: "预定战队",
    squadMembers: "战队成员",
    createSquad: "创建战队",
    squadName: "战队名称",
    squadDescription: "描述",
    discount: "折扣 (%)",
    addMembers: "添加成员",
    squadCreated: "战队创建成功！",
    leaderVerified: "队长已认证",
    trustScore: "信任分",
    leaderExplanation: "您将成为战队队长。除了您的报价外，您还将获得 5% 的管理费。",
    squadRequirements: "战队队长要求",
    increaseTrustDesc: "成功完成更多订单以提高您的信任分。",
    squadDetails: "战队详情",
    meetTheSquad: "认识战队",
    requestToJoin: "申请加入",
    applicationSent: "申请已发送！",
    alreadyMember: "已经是成员",
    manageApplications: "管理申请",
    pendingApplications: "待处理申请",
    noApplications: "暂无待处理申请。",
    applicationReason: "申请理由",
    whyJoinSquad: "为什么您想加入这个战队？",
    approve: "批准",
    reject: "拒绝",
    joinRequestSent: "加入请求已发送",
    joinRequestDesc: "您的申请已发送给战队队长进行审核。",
    revenueSplit: "收入分配",
    myRate: "我的战队报价",
    myRateDesc: "您在折扣前的总价值份额。",
    estimatedPayout: "预估收入",
    proportionalShare: "净收入份额",
    leaderFee: "队长费 (5%)",
    platformFeeLabel: "平台费 (20%)",
    enterLeaderRate: "您的报价 (队长)",
    enterProposedRate: "提议报价 (USDT)",
    totalSquadValue: "战队总价值",
    
    // Squad Booking
    squadBookingTitle: "预定战队活动",
    squadSummary: "战队摘要",
    totalPrice: "总价",
    campaignRequirements: "活动要求",
    campaignDeadline: "截止日期",
    confirmBooking: "确认预定",
    bookingSuccess: "战队预定成功！",

    // Referral
    inviteEarn: "邀请赚钱",
    referralSubtitle: "分享您的专属链接。邀请任何人，即可永久获得其平台费用的 5% 作为佣金。",
    referralSubtitleProject: "邀请高质量 KOL 加入平台。从他们参与的任何项目中赚取 5% 的佣金。",
    yourLink: "您的邀请链接",
    totalInvites: "总邀请",
    commissionEarned: "已赚佣金",
    recentInvites: "近期邀请",

    // Marketplace (Project finding KOL)
    findTalent: "寻找人才",
    findTalentSubtitle: "数据驱动发现，不再猜测。",
    searchPlaceholder: "搜索姓名、标签...",
    trust: "信用",
    followers: "粉丝",
    engScore: "互动分",
    estPrice: "预估价",
    viewProfile: "查看资料",
    backToMarketplace: "返回市场",
    sortBy: "排序",
    sortRecommended: "推荐",
    sortTrustHigh: "信用分 (从高到低)",
    sortFollowersHigh: "粉丝数 (从高到低)",
    sortPriceLow: "价格 (从低到高)",

    // Project Marketplace (KOL finding Project)
    findProjectsTitle: "寻找项目",
    findProjectsSubtitle: "浏览活跃活动并直接申请。",
    searchProjectsPlaceholder: "搜索项目...",
    activeCampaigns: "活跃活动",
    grabMode: "抢单模式",
    applyNow: "立即申请",
    budgetRange: "预算范围",
    
    // My Campaigns
    backToDashboard: "返回仪表盘",
    campaignBudget: "活动预算",
    escrowed: "已托管",
    notEscrowed: "未托管",
    applicants: "申请人",
    proposedRate: "提议价格",
    sendMessage: "发送消息",
    acceptOffer: "接受报价",
    noApplicants: "未找到申请人。",
    viewApplicants: "查看申请人",

    // Orders
    contractManagement: "合约管理",
    kolColumn: "KOL",
    projectColumn: "项目",
    campaignColumn: "活动",
    statusColumn: "状态",
    amountColumn: "金额",
    dueDateColumn: "截止日期",
    actionsColumn: "操作",
    details: "详情",
    leaveReview: "评价",
    reviewSubmitted: "评价已提交",

    // Contract Status & Steps
    stepPending: "等待接受",
    stepNegotiating: "谈判中",
    stepEscrow: "托管中",
    stepInProgress: "进行中",
    stepSubmitted: "已提交",
    stepCompleted: "已完成",
    contractProgress: "合约进度",
    autoReleaseTimer: "自动放款计时器",
    autoReleaseDesc: "如果未采取行动，资金将自动释放。",
    markAsDone: "提交工作",
    confirmRelease: "确认并放款",
    enterArbitration: "申请仲裁",
    submissionReceived: "收到提交",
    waitingForApproval: "等待项目方批准",

    // Booking & Staking
    bookServiceTitle: "预定服务",
    stakingRequired: "需质押资金",
    stakingDesc: "为确保信任，必须将全额资金质押到智能合约托管中。只有在工作获批时，资金才会释放。",
    serviceDetails: "服务详情",
    totalToStake: "质押总额",
    stakeAndBook: "质押并预定",
    insufficientBalance: "余额不足",
    confirmStaking: "确认质押",
    stakingProcessing: "质押处理中...",
    bookingSent: "预定已发送！",
    bookingSentDesc: "资金已质押到托管合约。等待 KOL 接受。",

    // Monitoring
    monitoringDashboard: "合约详情",
    aiSupervisor: "AI 监管",
    statusNormal: "正常",
    statusRisk: "有风险",
    statusCritical: "严重",
    views: "浏览量",
    current: "当前",
    target: "目标",
    likes: "点赞",
    engagement: "互动率",
    timeElapsed: "已用时间",
    liveTracking: "实时追踪",

    // Service Modal
    editService: "编辑服务",
    addService: "添加服务",
    serviceTitle: "服务标题",
    selectPlatform: "选择平台",
    priceUsdt: "价格 (USDT)",
    deliveryTime: "交付时间 (天)",
    description: "描述",
    featuresList: "特点 (每行一个)",
    cancel: "取消",
    saveService: "保存服务",
    delivery: "交付",
    bookNow: "立即预定",

    // Profile Edit
    editProfile: "编辑资料",
    editProfileTitle: "编辑个人资料",
    displayName: "显示名称",
    handle: "用户名",
    bio: "简介",
    tags: "标签",
    minPrice: "最低价",
    maxPrice: "最高价",
    saveChanges: "保存更改",
    aiTags: "AI 自动标签",
    aiTagsAnalyzing: "分析中...",
    tagsPlaceholder: "按回车添加标签",

    // Platform Connect
    connectPlatformTitle: "连接社交账号",
    verifyAccount: "验证您的所有权",
    startVerification: "开始验证",
    verifying: "验证中...",
    fetchData: "获取数据自",
    verifiedSuccess: "验证成功！",
    connect: "连接",

    // Review
    reviewTitle: "留下评价",
    reviewFor: "评价对象",
    rating: "评分",
    comment: "评论",
    commentPlaceholder: "分享您与此 KOL 的合作经历...",
    submitReview: "提交评价",

    // Apply
    applyTo: "申请项目",
    pitchMessage: "推介信息",
    pitchPlaceholder: "为什么您适合这个活动？",
    sendApplication: "发送申请",

    // Settings
    generalSettings: "常规",
    walletSettings: "钱包",
    notificationSettings: "通知",
    securitySettings: "安全",
    accountInfo: "账户信息",
    email: "电子邮件",
    language: "语言",
    currency: "货币",
    connectedWallet: "已连接钱包",
    disconnectWallet: "断开连接",
    emailNotifications: "邮件通知",
    pushNotifications: "推送通知",
    marketingEmails: "营销邮件",
    telegramSettings: "Telegram 集成",
    telegramHandle: "Telegram 用户名",
    telegramConnected: "已连接",
    connectBot: "连接机器人",
    twoFactorAuth: "双重认证",
    changePassword: "修改密码",
    savePreferences: "保存偏好",
    preferencesSaved: "已保存",

    // Chat
    chats: "聊天",
    searchContacts: "搜索联系人...",
    online: "在线",
    typeMessage: "输入消息...",
    noChatSelected: "选择一个聊天开始消息",
    translatedMessage: "已翻译",

    // Helpers
    unknown: "未知",
    
    // AI Assistant
    chatWithAi: "LinkKol AI 助手",
    askAi: "询问 AI",
    askAiPlaceholder: "询问 KOL 表现...",
    aiSummary: "AI 摘要",
    strengths: "优势",
    weaknesses: "劣势",
    clientScore: "客户评分",
    aiScore: "AI 评分",
    
    // AI Analysis Metrics (New)
    authenticity: "真实性",
    sentiment: "情感倾向",
    commercialValue: "商业价值",
    brandSafety: "品牌安全",
    consistency: "内容一致性",

    // LinkKol Points
    linkKolPoints: "LinkKol 积分",
    pointsBalance: "积分余额",
    viewRewards: "查看奖励",
    convertToToken: "兑换 LNK",
    comingSoon: "即将推出",
    pointsEarned: "获得积分",
    
    // Other
    noReviews: "暂无评价。",
    performanceHistory: "历史表现",
    clientReviews: "客户评价",
    services: "服务",
    servicesSubtitle: "此创作者提供的标准服务包",
    telegramAlertSent: "Telegram 提醒已发送给"
};

export const TRANSLATIONS: Record<Language, any> = {
  en,
  zh,
  'zh-TW': en, // Fallback
  ko: en, // Fallback
  ja: en, // Fallback
  vi: en, // Fallback
  ru: en, // Fallback
  tr: en, // Fallback
  fr: en, // Fallback
  es: en, // Fallback
  ar: en, // Fallback
  de: en, // Fallback
  id: en, // Fallback
  ms: en, // Fallback
  th: en, // Fallback
  pt: en, // Fallback
  it: en, // Fallback
  hi: en  // Fallback
};
